import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'

const ESCAPE_KEY = 'Escape'

export interface NavigationMenuState {
  readonly expanded: boolean
  readonly toggle: () => void
  /**
   * Collapses without touching focus.
   *
   * Used when a section link is activated, where the anchor jump has already
   * decided where the reader is going. Moving focus back to the toggle there
   * would undo the navigation they just asked for.
   */
  readonly close: () => void
  /** Attach to the disclosure button so `Escape` has somewhere to put focus. */
  readonly toggleRef: RefObject<HTMLButtonElement | null>
}

/**
 * Open/closed state for the mobile navigation disclosure.
 *
 * NOTHING IS PERSISTED AND THE INITIAL STATE IS ALWAYS COLLAPSED — the same
 * contract as `useReadingScale`, for the same two reasons. The site is prerendered
 * to static HTML, so a state read from storage during render would differ from
 * what the server wrote and mismatch on hydration; and a menu that remembers
 * having been open would push the page down on first paint, which is a layout
 * shift against a budget that is exactly 0.
 *
 * ABOVE THE BREAKPOINT THIS HOOK STILL RUNS AND STILL DOES NOTHING VISIBLE. The
 * stylesheet shows the navigation unconditionally at 52em and hides the toggle, so
 * `expanded` stops being consulted. That is deliberate: the alternative is
 * matching a media query in JavaScript, which means a second source of truth for
 * the breakpoint and a resize listener to keep it honest.
 */
export function useNavigationMenu(): NavigationMenuState {
  const [expanded, setExpanded] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)

  const toggle = useCallback(() => {
    setExpanded((open) => !open)
  }, [])

  const close = useCallback(() => {
    setExpanded(false)
  }, [])

  /**
   * Synchronises with the document's key events.
   *
   * A genuine external system (rules/frontend.md § 2): `Escape` has to be caught
   * wherever focus happens to be, and while the menu is open that includes the
   * four links inside the panel — which no React handler on the toggle can see.
   * The listener is attached only while there is something to close.
   */
  useEffect(() => {
    if (!expanded) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== ESCAPE_KEY) return

      setExpanded(false)
      // The panel is one render away from `display: none`. Focus standing inside
      // it would be dropped on `<body>`, so it is put somewhere deliberate first
      // — § 2.4.3 Focus Order, and the reason `toggleRef` is returned at all.
      toggleRef.current?.focus()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [expanded])

  return useMemo(
    () => ({ expanded, toggle, close, toggleRef }),
    [expanded, toggle, close],
  )
}
